import clsx from "clsx";
import { PurchaseItemStatus } from "common";
import { isNil } from "lodash";
import React, { useEffect, useState } from "react";
import { GratuityAlgorithm, PurchaseItem } from "types";
import { Currency } from "../Common/Currency";
import { SecondaryButton } from "../Common/SecondaryButton";
import { CustomGratuitySelector } from "@/components/PurchaseCreate/CustomGratuitySelector";
import { CreatePurchaseItemState, GratuitySelection, useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { getSeller } from "@/utils/getSeller";

interface GratuitySelectorProps {
    item: CreatePurchaseItemState | PurchaseItem;
    itemUnitSubtotal: number;
    onGratuityChange: (gratuitySelection: GratuitySelection) => void;
}

export const getValueFromGratuitySelection = (gratuitySelection: GratuitySelection, subtotal: number) => {
    let value = gratuitySelection.amount;
    if (gratuitySelection.algorithm === GratuityAlgorithm.PERCENT) {
        value = gratuitySelection.amount * 0.01 * subtotal;
    }
    return value;
};

export const GratuitySelector = ({ item, itemUnitSubtotal, onGratuityChange }: GratuitySelectorProps) => {
    const seller = getSeller();
    const selection = useCreatePurchaseStore((state) => state.getItemGratuitySelection(item?.id ?? ""));
    const [gratuitySelection, setGratuitySelection] = useState<GratuitySelection>();
    useEffect(() => {
        // set selection to the last selected value from store
        // when switching to another item
        if (selection) {
            setGratuitySelection(selection);
        }
    }, [selection, item]);

    useEffect(() => {
        if (!gratuitySelection) return;
        onGratuityChange(gratuitySelection);
    }, [itemUnitSubtotal, gratuitySelection, onGratuityChange]);

    const isSelected = (amount: number, algorithm: GratuityAlgorithm) =>
        gratuitySelection?.amount === amount && gratuitySelection?.algorithm === algorithm && !gratuitySelection.custom;

    return (
        <div className="gratuity-selector">
            <div className="flex justify-between">
                <h3 className="mr-4 mb-4">Gratuity</h3>
            </div>
            <div className="flex w-full flex-wrap gap-x-1 gap-y-1">
                {seller.preferences.gratuity.options.map((option) => (
                    <SecondaryButton
                        key={option.amount + option.algorithm}
                        className={clsx(
                            "w-28",
                            isSelected(option.amount, option.algorithm)
                                ? "border border-blue-light bg-blue-lighter hover:bg-blue-lighter"
                                : "",
                        )}
                        onClick={() =>
                            setGratuitySelection({ amount: option.amount, algorithm: option.algorithm, custom: false })
                        }
                    >
                        {option.algorithm === GratuityAlgorithm.ABSOLUTE ? (
                            <Currency>{option.amount}</Currency>
                        ) : (
                            <div className="flex items-center space-x-0.5">
                                <span>{option.amount}%</span>
                                {!isNil(itemUnitSubtotal) && (
                                    <span className="text-sm font-medium text-gray-darker">
                                        (<Currency>{option.amount * 0.01 * itemUnitSubtotal}</Currency>)
                                    </span>
                                )}
                            </div>
                        )}
                    </SecondaryButton>
                ))}
                <CustomGratuitySelector
                    itemUnitSubtotal={itemUnitSubtotal}
                    selection={gratuitySelection}
                    onSelection={(amount, algorithm, custom) =>
                        setGratuitySelection({ amount: amount, algorithm: algorithm, custom: custom })
                    }
                />
                {item.status === PurchaseItemStatus.DRAFT && (
                    <SecondaryButton
                        className={clsx(
                            "w-28 !p-1",
                            isSelected(0, GratuityAlgorithm.ABSOLUTE)
                                ? "border border-blue-light bg-blue-lighter hover:bg-blue-lighter"
                                : "",
                        )}
                        onClick={() =>
                            setGratuitySelection({ amount: 0, algorithm: GratuityAlgorithm.ABSOLUTE, custom: false })
                        }
                    >
                        No Gratuity
                    </SecondaryButton>
                )}
            </div>
        </div>
    );
};
